import React, { useState } from 'react';
import {
  Search,
  Filter,
  X,
  RotateCcw,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useReconciliation } from '../../context/ReconciliationContext';
import { ReconciliationStatus } from '../../types/reconciliation';

export const FilterBar: React.FC = () => {
  const { filterState, updateFilters, resetFilters, filteredItems, items } = useReconciliation();
  const [isExpanded, setIsExpanded] = useState(false);

  const statusOptions: ReconciliationStatus[] = [
    'Exact Match',
    'Partial Match',
    'Over Billed',
    'No Bill Reference',
    'ERP Only',
    'Refunded',
  ];

  const toggleStatus = (status: ReconciliationStatus) => {
    const current = filterState.statuses;
    if (current.includes(status)) {
      updateFilters({ statuses: current.filter((s) => s !== status) });
    } else {
      updateFilters({ statuses: [...current, status] });
    }
  };

  const hasActiveFilters =
    !!filterState.searchQuery ||
    !!filterState.paymentId ||
    !!filterState.billNumber ||
    !!filterState.customerName ||
    filterState.statuses.length > 0 ||
    filterState.minAmount !== undefined ||
    filterState.maxAmount !== undefined ||
    !!filterState.startDate ||
    !!filterState.endDate ||
    filterState.refundStatus !== 'all';

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mb-5">
      {/* Primary Search Bar */}
      <div className="p-3.5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={filterState.searchQuery}
            onChange={(e) => updateFilters({ searchQuery: e.target.value })}
            placeholder="Quick search Payment ID, Customer Name, or Bill Number..."
            className="w-full pl-10 pr-4 py-2 rounded-xl text-xs bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          {filterState.searchQuery && (
            <button
              onClick={() => updateFilters({ searchQuery: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition ${
              isExpanded || hasActiveFilters
                ? 'bg-brand-50 text-brand-700 border-brand-300 dark:bg-brand-950/60 dark:text-brand-300 dark:border-brand-800'
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            <span>Advanced Filters</span>
            {filterState.statuses.length > 0 && (
              <span className="w-4 h-4 rounded-full bg-brand-600 text-white text-[10px] flex items-center justify-center font-bold">
                {filterState.statuses.length}
              </span>
            )}
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 px-2.5 py-2 rounded-xl text-xs font-medium text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition"
              title="Reset all filters"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          )}

          <div className="text-xs font-mono text-slate-500 dark:text-slate-400 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
            Showing <strong className="text-slate-900 dark:text-white">{filteredItems.length}</strong> of{' '}
            {items.length}
          </div>
        </div>
      </div>

      {/* Advanced Filter Drawer */}
      {isExpanded && (
        <div className="p-4 pt-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 space-y-4 animate-fade-in">
          {/* Status Multi-Select Pills */}
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 block">
              Filter by Reconciliation Status
            </span>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((status) => {
                const isSelected = filterState.statuses.includes(status);
                return (
                  <button
                    key={status}
                    onClick={() => toggleStatus(status)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-brand-600 text-white shadow-xs font-semibold'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <span>{status}</span>
                    {isSelected && <X className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Granular Field Inputs Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-slate-200/60 dark:border-slate-800">
            {/* Payment ID Specific */}
            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Payment ID
              </label>
              <input
                type="text"
                value={filterState.paymentId}
                onChange={(e) => updateFilters({ paymentId: e.target.value })}
                placeholder="e.g. RP001..."
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Bill Number */}
            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                ERP Bill Number
              </label>
              <input
                type="text"
                value={filterState.billNumber}
                onChange={(e) => updateFilters({ billNumber: e.target.value })}
                placeholder="e.g. BILL-2026..."
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Customer Name */}
            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Customer Name
              </label>
              <input
                type="text"
                value={filterState.customerName}
                onChange={(e) => updateFilters({ customerName: e.target.value })}
                placeholder="e.g. Rajesh Sharma..."
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Refund Status */}
            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Refund Filter
              </label>
              <select
                value={filterState.refundStatus || 'all'}
                onChange={(e) => updateFilters({ refundStatus: e.target.value as any })}
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              >
                <option value="all">All Transactions</option>
                <option value="refunded">Refunded Only</option>
                <option value="not_refunded">Non-Refunded</option>
              </select>
            </div>
          </div>

          {/* Amount Range & Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-slate-200/60 dark:border-slate-800">
            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Min Amount (₹)
              </label>
              <input
                type="number"
                value={filterState.minAmount ?? ''}
                onChange={(e) => updateFilters({ minAmount: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="0"
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Max Amount (₹)
              </label>
              <input
                type="number"
                value={filterState.maxAmount ?? ''}
                onChange={(e) => updateFilters({ maxAmount: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="100,000+"
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filterState.startDate || ''}
                onChange={(e) => updateFilters({ startDate: e.target.value })}
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 block mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filterState.endDate || ''}
                onChange={(e) => updateFilters({ endDate: e.target.value })}
                className="w-full px-3 py-1.5 rounded-lg text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
